import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTasks } from '../../hooks/useTasks';
import { Project, PROJECT_STATUS_OPTIONS, PROJECT_PRIORITY_OPTIONS } from '../../types/project';
import { formatDateTime, formatDate, isOverdue } from '../../utils/dateHelpers';
import ProjectTasksModal from './ProjectTasksModal';
import { 
  PencilSquareIcon, 
  TrashIcon, 
  EyeIcon,
  UserIcon,
  CalendarIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  CurrencyDollarIcon,
  TagIcon,
  ListBulletIcon
} from '@heroicons/react/24/outline';

interface ProjectListProps {
  projects: Project[];
  loading: boolean;
  onEdit: (project: Project) => void;
  onDelete: (projectId: string) => void;
  onView: (project: Project) => void;
  onUpdateStatus: (projectId: string, status: Project['status']) => void;
  onUpdatePriority: (projectId: string, priority: Project['priority']) => void;
}

const ProjectList: React.FC<ProjectListProps> = ({
  projects,
  loading,
  onEdit,
  onDelete,
  onView,
  onUpdateStatus,
  onUpdatePriority
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { tasks } = useTasks();
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [tasksModalProject, setTasksModalProject] = useState<Project | null>(null);

  // Projekthez tartozó taskok számának lekérése
  const getTaskCount = (projectId: string) => {
    return tasks.filter(task => task.projectId === projectId).length;
  };

  // Státusz szín lekérése
  const getStatusColor = (status: Project['status']) => {
    const isDark = document.documentElement.classList.contains('dark');
    
    switch (status) {
      case 'planning': 
        return isDark ? '#9ca3af' : '#94a3b8'; // gray
      case 'active': 
        return isDark ? '#60a5fa' : '#2563eb'; // blue
      case 'completed': 
        return isDark ? '#34d399' : '#16a34a'; // green
      case 'paused': 
        return isDark ? '#fbbf24' : '#eab308'; // yellow
      case 'cancelled': 
        return isDark ? '#f87171' : '#dc2626'; // red
      default: 
        return isDark ? '#9ca3af' : '#94a3b8';
    }
  };

  // Prioritás szín lekérése
  const getPriorityColor = (priority: Project['priority']) => {
    const isDark = document.documentElement.classList.contains('dark');
    
    switch (priority) {
      case 'low': 
        return isDark ? '#34d399' : '#22c55e'; // green
      case 'medium': 
        return isDark ? '#fbbf24' : '#eab308'; // yellow
      case 'high': 
        return isDark ? '#fb923c' : '#f97316'; // orange
      case 'urgent': 
        return isDark ? '#f87171' : '#ef4444'; // red
      default: 
        return isDark ? '#34d399' : '#22c55e';
    }
  };

  // Kombinált border stílus
  const getCombinedBorderStyle = (project: Project) => ({
    borderLeftColor: getStatusColor(project.status),
    borderRightColor: getPriorityColor(project.priority)
  });

  // Progressz szín lekérése
  const getProgressColor = (progress: number) => {
    if (progress < 30) return 'bg-red-500';
    if (progress < 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  // Expand/collapse kezelés
  const toggleExpand = (projectId: string) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  // Státusz megjelenítés
  const getStatusLabel = (status: Project['status']) => {
    return PROJECT_STATUS_OPTIONS.find(opt => opt.value === status)?.label || status;
  };

  // Prioritás megjelenítés
  const getPriorityLabel = (priority: Project['priority']) => {
    return PROJECT_PRIORITY_OPTIONS.find(opt => opt.value === priority)?.label || priority;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500">
          <ChartBarIcon className="h-12 w-12" />
        </div>
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          {t('projects.noProjects') || 'Nincsenek projektek'}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('projects.noProjectsDescription') || 'Kezdjen el egy új projekt létrehozásával.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Desktop táblázat */}
      <div className="hidden xl:block bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                {t('projects.fields.name') || 'Projekt'}
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                {t('projects.fields.status') || 'Státusz'}
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                {t('projects.fields.priority') || 'Prioritás'}
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                {t('projects.fields.progress') || 'Haladás'}
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                {t('projects.fields.endDate') || 'Határidő'}
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                {t('projects.fields.customer') || 'Ügyfél'}
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                {t('projects.fields.tasks') || 'Feladatok'}
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                {t('projects.fields.budget') || 'Költségvetés'}
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                {t('common.actions') || 'Műveletek'}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {projects.map((project) => (
              <tr
                key={project.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700 border-l-4 border-r-4"
                style={getCombinedBorderStyle(project)}
              >
                <td className="px-3 py-2">
                  <div className="flex flex-col">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {project.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {project.description}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2">
                  <select
                    value={project.status}
                    onChange={(e) => onUpdateStatus(project.id, e.target.value as Project['status'])}
                    className="text-xs font-medium rounded-full px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                    style={{
                      backgroundColor: getStatusColor(project.status) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                      color: getStatusColor(project.status)
                    }}
                  >
                    {PROJECT_STATUS_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <select
                    value={project.priority}
                    onChange={(e) => onUpdatePriority(project.id, e.target.value as Project['priority'])}
                    className="text-xs font-medium rounded-full px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                    style={{
                      backgroundColor: getPriorityColor(project.priority) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                      color: getPriorityColor(project.priority)
                    }}
                  >
                    {PROJECT_PRIORITY_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${getProgressColor(project.progress)}`}
                        style={{ width: `${project.progress}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-900 dark:text-gray-100 w-8 text-right">
                      {project.progress}%
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="text-xs text-gray-900 dark:text-gray-100">
                    {formatDate(project.endDate)}
                  </div>
                  {isOverdue(project.endDate) && project.status !== 'completed' && (
                    <div className="text-xs text-red-600 dark:text-red-400 font-medium">
                      {t('projects.overdue') || 'Lejárt'}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="text-xs text-gray-900 dark:text-gray-100 truncate">
                    {project.customerName || '-'}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center space-x-1">
                    <ListBulletIcon className="h-3 w-3 text-teal-600 dark:text-teal-400" />
                    <span className="text-xs text-gray-900 dark:text-gray-100">
                      {getTaskCount(project.id)}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="text-xs text-gray-900 dark:text-gray-100">
                    {project.budget ? `${project.budget.toLocaleString()} Ft` : '-'}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex space-x-1">
                    <button
                      onClick={() => onView(project)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      title={t('common.view') || 'Megtekintés'}
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setTasksModalProject(project)}
                      className="text-teal-600 dark:text-teal-400 hover:text-teal-900 dark:hover:text-teal-300 p-1 rounded hover:bg-teal-50 dark:hover:bg-teal-900/20"
                      title={t('projects.actions.manageTasks') || 'Feladatok kezelése'}
                    >
                      <ListBulletIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onEdit(project)}
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 p-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                      title={t('common.edit') || 'Szerkesztés'}
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onDelete(project.id)}
                      className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                      title={t('common.delete') || 'Törlés'}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Mobil kártyák */}
      <div className="xl:hidden space-y-4">
        {projects.map((project) => (
          <div
            key={project.id}
            className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-md border border-gray-200 dark:border-gray-700 border-l-4 border-r-4"
            style={getCombinedBorderStyle(project)}
          >
            {/* Projekt fejléc */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {project.name}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {project.description}
                </p>
              </div>
              <div className="flex space-x-1 ml-2">
                <button
                  onClick={() => onView(project)}
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                >
                  <EyeIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setTasksModalProject(project)}
                  className="text-teal-600 dark:text-teal-400 hover:text-teal-900 dark:hover:text-teal-300 p-1 rounded hover:bg-teal-50 dark:hover:bg-teal-900/20"
                >
                  <ListBulletIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onEdit(project)}
                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 p-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onDelete(project.id)}
                  className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Státusz és prioritás */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                  {t('projects.fields.status') || 'Státusz'}
                </label>
                <select
                  value={project.status}
                  onChange={(e) => onUpdateStatus(project.id, e.target.value as Project['status'])}
                  className="text-xs font-medium rounded-full px-1.5 py-0.5 w-full focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                  style={{
                    backgroundColor: getStatusColor(project.status) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                    color: getStatusColor(project.status)
                  }}
                >
                  {PROJECT_STATUS_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                  {t('projects.fields.priority') || 'Prioritás'}
                </label>
                <select
                  value={project.priority}
                  onChange={(e) => onUpdatePriority(project.id, e.target.value as Project['priority'])}
                  className="text-xs font-medium rounded-full px-1.5 py-0.5 w-full focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                  style={{
                    backgroundColor: getPriorityColor(project.priority) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                    color: getPriorityColor(project.priority)
                  }}
                >
                  {PROJECT_PRIORITY_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Haladás */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">
                  {t('projects.fields.progress') || 'Haladás'}
                </span>
                <span className="text-xs font-medium text-gray-900">
                  {project.progress}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${getProgressColor(project.progress)}`}
                  style={{ width: `${project.progress}%` }}
                />
              </div>
            </div>

            {/* További részletek */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <CalendarIcon className="h-4 w-4 text-gray-400" />
                <div className="flex-1 text-xs text-gray-600">
                  {formatDate(project.endDate)}
                  {isOverdue(project.endDate) && project.status !== 'completed' && (
                    <span className="text-red-600 font-medium ml-1">
                      ({t('projects.overdue') || 'Lejárt'})
                    </span>
                  )}
                </div>
              </div>

              {project.customerName && (
                <div className="flex items-center space-x-2">
                  <BuildingOffice2Icon className="h-4 w-4 text-gray-400" />
                  <div className="flex-1 text-xs text-gray-600">
                    {project.customerName}
                  </div>
                </div>
              )}

              {project.budget && (
                <div className="flex items-center space-x-2">
                  <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
                  <div className="flex-1 text-xs text-gray-600">
                    {project.budget.toLocaleString()} Ft
                  </div>
                </div>
              )}

              {project.assignedUsersNames && project.assignedUsersNames.length > 0 && (
                <div className="flex items-center space-x-2">
                  <UserIcon className="h-4 w-4 text-gray-400" />
                  <div className="flex-1 text-xs text-gray-600">
                    {project.assignedUsersNames.join(', ')}
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <ListBulletIcon className="h-4 w-4 text-teal-600" />
                <div className="flex-1 text-xs text-gray-600">
                  {getTaskCount(project.id)} {t('projects.fields.tasks') || 'feladat'}
                </div>
              </div>

              {project.tags.length > 0 && (
                <div className="flex items-center space-x-2">
                  <TagIcon className="h-4 w-4 text-gray-400" />
                  <div className="flex-1 flex flex-wrap gap-1">
                    {project.tags.map(tag => (
                      <span
                        key={tag}
                        className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Project Tasks Modal */}
      {tasksModalProject && (
        <ProjectTasksModal
          project={tasksModalProject}
          isOpen={!!tasksModalProject}
          onClose={() => setTasksModalProject(null)}
        />
      )}
    </div>
  );
};

export default ProjectList; 